import { defineStore } from 'pinia';
import api from '../api';

// Deliberately brand-neutral: this build also serves the white-label demo
// (customer.safirios.com), and a hardcoded "Riley TMS" here flashed on the first paint of
// a first-ever visit — before GET /config resolves — on someone else's portal.
const DEFAULT_COMPANY_NAME = 'Client Portal';
const CACHE_KEY = '_company_name';

/**
 * App-wide config pulled once from the API (GET /config). Holds the
 * settings-configured company name so brand text isn't hardcoded.
 * The last-known name is cached in localStorage and restored on init, so the
 * correct brand renders instantly on fresh loads such as the /terms page opening
 * in a new tab. Falls back to the neutral default only on a first-ever visit or
 * if the setting is missing/blank.
 */
export const useConfigStore = defineStore('configStore', {
  state: () => ({
    companyName: (typeof localStorage !== 'undefined' && localStorage.getItem(CACHE_KEY)) || DEFAULT_COMPANY_NAME,
    // Live-chat widget visibility — controlled from TMS Settings, OFF by default.
    chatEnabled: false,
    loaded: false,
  }),
  getters: {
    // Always non-empty: falls back to the default if the setting is missing/blank.
    getCompanyName: (state) => state.companyName || DEFAULT_COMPANY_NAME,
  },
  actions: {
    async fetchConfig() {
      try {
        const res = await api.get('/config');
        const name = res.data?.data?.company_name;
        if (typeof name === 'string' && name.trim()) {
          this.companyName = name.trim();
          try { localStorage.setItem(CACHE_KEY, this.companyName); } catch { /* ignore */ }
        }
        this.chatEnabled = res.data?.data?.customer_chat_enabled === true;
      } catch {
        // silently fall back to the default company name
      } finally {
        this.loaded = true;
        // Reflect the brand in the browser tab title.
        document.title = this.getCompanyName;
      }
    },
  },
});
